import React, { useRef, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import "./Settings.css";
import settingAvatar from "../../assets/images/setting-image.png";
import {
  useGetCompanyPositionsQuery,
  useSelectMyPositionMutation,
  useUploadAvatarMutation,
  useUpdateProfileMutation,
} from "../../services/api";
import { updateUser } from "../../store/slices/authSlice";

export default function Settings() {
  const fileRef = useRef(null);
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);

  // Form state initialized from user data
  const [form, setForm] = useState({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
  });

  // Position selection state
  const [selectedPosition, setSelectedPosition] = useState(user?.position?._id || user?.position || "");
  const [positionSaved, setPositionSaved] = useState(false);

  // Fetch positions for user's company
  const companyId = user?.company?._id || user?.company;
  const { data: positions = [], isLoading: positionsLoading } = useGetCompanyPositionsQuery(
    companyId,
    { skip: !companyId }
  );

  // Mutations
  const [selectMyPosition, { isLoading: isSavingPosition }] = useSelectMyPositionMutation();
  const [uploadAvatar, { isLoading: isUploading }] = useUploadAvatarMutation();
  const [updateProfile, { isLoading: isSavingProfile }] = useUpdateProfileMutation();

  // Avatar preview state
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [nameSaved, setNameSaved] = useState(false);

  const [pushEnabled, setPushEnabled] = useState(false);
  const [dndEnabled, setDndEnabled] = useState(false);
  const [theme, setTheme] = useState("light"); // 'light' | 'dark'
  const [password] = useState("".padStart(16, "•"));

  // Update form when user data changes
  useEffect(() => {
    if (user) {
      setForm({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
      });
      setSelectedPosition(user.position?._id || user.position || "");
    }
  }, [user]);

  // Handle position change
  const handlePositionChange = async (e) => {
    const newPositionId = e.target.value;
    setSelectedPosition(newPositionId);
    setPositionSaved(false);

    if (newPositionId) {
      try {
        await selectMyPosition({ positionId: newPositionId }).unwrap();
        setPositionSaved(true);
        setTimeout(() => setPositionSaved(false), 3000);
      } catch (err) {
        console.error("Failed to update position:", err);
      }
    }
  };

  const onPickFile = () => fileRef.current?.click();

  // Handle avatar file selection and upload
  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Show local preview immediately
    const reader = new FileReader();
    reader.onloadend = () => setAvatarPreview(reader.result);
    reader.readAsDataURL(file);

    // Upload to server
    try {
      const formData = new FormData();
      formData.append("avatar", file);
      const result = await uploadAvatar(formData).unwrap();
      if (result.avatarUrl) {
        dispatch(updateUser({ avatar: result.avatarUrl }));
      }
    } catch (err) {
      console.error("Failed to upload avatar:", err);
      setAvatarPreview(null);
    }
  };

  // Handle name save
  const handleSaveName = async () => {
    try {
      await updateProfile({
        firstName: form.firstName,
        lastName: form.lastName,
      }).unwrap();
      dispatch(updateUser({ firstName: form.firstName, lastName: form.lastName }));
      setNameSaved(true);
      setTimeout(() => setNameSaved(false), 3000);
    } catch (err) {
      console.error("Failed to update profile:", err);
    }
  };

  // Compute avatar display URL
  const avatarSrc = avatarPreview
    || (user?.avatar?.startsWith("/") ? `${process.env.REACT_APP_API_URL || "http://localhost:5045"}${user.avatar}` : user?.avatar)
    || settingAvatar;

  return (
    <div className="settings-container">
      <button className="welcome">← Back Home</button>
      <h1 className="username">Settings</h1>

      {/* Personal Info */}
      <section className="settings-card">
        <div className="settings-card-head">Personal Information</div>

        <div className="settings-content">
          <div className="settings-form">
            <div className="row-2">
              <div className="field">
                <label>First Name</label>
                <input
                  value={form.firstName}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, firstName: e.target.value }))
                  }
                  className="input"
                  placeholder="First name"
                />
              </div>
              <div className="field">
                <label>Last Name</label>
                <input
                  value={form.lastName}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, lastName: e.target.value }))
                  }
                  className="input"
                  placeholder="Last name"
                />
              </div>
            </div>

            <div className="field">
              <label>Email Address</label>
              <input value={form.email} disabled className="input" />
            </div>

            <div className="field password-field">
              <label>Password</label>
              <div className="password-input-wrapper">
                <input value={password} readOnly className="input" />
                <button
                  type="button"
                  className="btn-change"
                  onClick={() => alert("Open change password modal")}
                >
                  Change
                </button>
              </div>
            </div>

            {/* Save name changes */}
            {(form.firstName !== (user?.firstName || "") || form.lastName !== (user?.lastName || "")) && (
              <div className="field">
                <button
                  type="button"
                  className="btn-save-name"
                  onClick={handleSaveName}
                  disabled={isSavingProfile}
                >
                  {isSavingProfile ? "Saving..." : "Save Changes"}
                </button>
                {nameSaved && <span className="saved-indicator">Saved</span>}
              </div>
            )}

            {/* Job Position */}
            <div className="field">
              <label>Job Position</label>
              {positionsLoading ? (
                <div className="position-loading">Loading positions...</div>
              ) : positions.length > 0 ? (
                <div className="position-select-wrapper">
                  <select
                    value={selectedPosition}
                    onChange={handlePositionChange}
                    disabled={isSavingPosition}
                    className="input position-select"
                  >
                    <option value="">-- Select your position --</option>
                    {positions.map((pos) => (
                      <option key={pos._id} value={pos._id}>
                        {pos.name}
                      </option>
                    ))}
                  </select>
                  {isSavingPosition && <span className="saving-indicator">Saving...</span>}
                  {positionSaved && <span className="saved-indicator">✓ Saved</span>}
                </div>
              ) : (
                <div className="position-empty">No positions available for your company</div>
              )}
            </div>
          </div>

          {/* Avatar */}
          <div className="settings-avatar-section">
            <div className="avatar">
              <img
                src={avatarSrc}
                alt="User avatar"
              />
            </div>
            <button className="btn-change-avatar" onClick={onPickFile} disabled={isUploading}>
              {isUploading ? "Uploading..." : "Change"}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={handleAvatarChange}
              hidden
            />
          </div>
        </div>
      </section>

      {/* System Settings */}
      <section className="settings-card">
        <div className="settings-card-head">System Settings</div>

        <div className="settings-row between">
          <div className="setting-block">
            <div className="setting-title">Notification Settings</div>
          </div>
          <div className="setting-controls">
            <label className="check">
              <input
                type="checkbox"
                checked={pushEnabled}
                onChange={(e) => setPushEnabled(e.target.checked)}
              />
              <span>Push notifications</span>
            </label>
            <label className="check">
              <input
                type="checkbox"
                checked={dndEnabled}
                onChange={(e) => setDndEnabled(e.target.checked)}
              />
              <span>Do not disturb schedule</span>
            </label>
          </div>
        </div>

        <div className="divider" />

        <div className="settings-row between">
          <div className="setting-block">
            <div className="setting-title">Display Preferences</div>
          </div>
          <div className="setting-controls">
            <label className="radio">
              <input
                type="radio"
                name="theme"
                value="dark"
                checked={theme === "dark"}
                onChange={(e) => setTheme(e.target.value)}
              />
              <span>Dark mode</span>
            </label>
            <label className="radio">
              <input
                type="radio"
                name="theme"
                value="light"
                checked={theme === "light"}
                onChange={(e) => setTheme(e.target.value)}
              />
              <span>Light mode</span>
            </label>
          </div>
        </div>
      </section>
    </div>
  );
}
